import {useRef, useState} from "react";
import * as XLSX from "xlsx";
import {Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, TextField, Box, Table, TableRow, TableBody, TableCell} from "@mui/material";
import {asyncRequest} from "src/services/network";
import {alert} from "src/services/alert";

/**
 * 金银通批量拨付金豆弹窗
 * props: { open, onClose, onFinish }
 */
export default function JytBatchCoinRecharge({open, onClose, onFinish}) {
  const [batchItems, setBatchItems] = useState([]);
  const [batchPwd, setBatchPwd] = useState("");
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchOldDeadline, setBatchOldDeadline] = useState("");
  const [batchOldAmount, setBatchOldAmount] = useState("");
  const uploadRef = useRef(null);

  function handleBatchUpload(e) {
    if (!e.target.files[0]) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      if (uploadRef.current) uploadRef.current.value = null;
      try {
        const {result} = event.target;
        const workbook = XLSX.read(result, {type: "binary"});
        const items = [];
        for (const sheet in workbook.Sheets) {
          const sheetData = XLSX.utils.sheet_to_json(workbook.Sheets[sheet], {defval: ""});
          for (const row of sheetData) {
            const keys = Object.keys(row);
            if (keys.length < 2) continue;
            const uid = String(row[keys[0]] || "").trim();
            const amount = Number(row[keys[1]]);
            const remark = keys[2] ? String(row[keys[2]] || "").trim() : "";
            if (!uid || isNaN(amount) || amount === 0) continue;
            items.push({uid, amount, remark: remark || "批量拨付金豆"});
          }
          break;
        }
        setBatchItems(items);
      } catch (err) {
        console.error(err);
        alert("Excel文件解析失败，请检查格式", "error");
      }
    };
    reader.readAsBinaryString(e.target.files[0]);
  }

  async function handleBatchCoinRecharge() {
    if (batchItems.length === 0) { alert("请先上传Excel文件", "error"); return; }
    if (!batchPwd) { alert("请输入超管密码", "error"); return; }
    setBatchLoading(true);
    try {
      const data = {items: batchItems, password: batchPwd};
      if (batchOldDeadline && batchOldAmount) {
        data.oldUserDeadline = batchOldDeadline;
        data.oldUserAmount = Number(batchOldAmount);
      }
      const result = await asyncRequest({path: "/jingxi/jyt-user/batch-coin-recharge", data});
      if (result) {
        alert(result.msg || "操作成功", "success");
        setBatchItems([]);
        setBatchPwd("");
        setBatchOldDeadline("");
        setBatchOldAmount("");
        onClose();
        onFinish();
      }
    } catch (e) {
      if (e) alert(e.message || "操作失败", "error");
    } finally {
      setBatchLoading(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>批量拨付金豆</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{mt: 1}}>
          <Typography variant="body2" color="text.secondary">
            Excel第一行为列头（不解析），数据从第二行开始。三列：鲸世优选UID、新用户金豆数量（正数加/负数扣，最多两位小数）、备注（可选）
          </Typography>
          <Button variant="outlined" component="label">
            选择Excel文件
            <input type="file" hidden accept=".xlsx,.xls" ref={uploadRef} onChange={handleBatchUpload} />
          </Button>
          {batchItems.length > 0 && (
            <>
              <Typography variant="body2" sx={{fontWeight: "bold"}}>
                已解析 {batchItems.length} 条记录
              </Typography>
              <Box sx={{maxHeight: 200, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1}}>
                <Table size="small">
                  <TableBody>
                    {batchItems.slice(0, 20).map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{py: 0.3, px: 1}}>{item.uid}</TableCell>
                        <TableCell sx={{py: 0.3, px: 1, color: item.amount < 0 ? 'error.main' : 'success.main'}}>{item.amount}</TableCell>
                        <TableCell sx={{py: 0.3, px: 1}}>{item.remark}</TableCell>
                      </TableRow>
                    ))}
                    {batchItems.length > 20 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{py: 0.5, color: 'text.secondary'}}>
                          ...还有{batchItems.length - 20}条记录
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
          <Typography variant="body2" sx={{fontWeight: "bold", mt: 1}}>老用户设置（可选）</Typography>
          <Typography variant="body2" color="text.secondary">
            设置后，注册时间早于截止日期的用户视为老用户，按老用户金豆数量拨付；未设置则全部按Excel中金豆数量拨付
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              label="老用户截止日期"
              type="date"
              size="small"
              fullWidth
              value={batchOldDeadline}
              onChange={(e) => setBatchOldDeadline(e.target.value)}
              InputLabelProps={{shrink: true}}
            />
            <TextField
              label="老用户金豆数量"
              type="number"
              size="small"
              fullWidth
              value={batchOldAmount}
              onChange={(e) => setBatchOldAmount(e.target.value)}
              placeholder="如：100"
              inputProps={{step: "0.01"}}
            />
          </Stack>
          <TextField label="超管密码" type="password" size="small" fullWidth value={batchPwd} onChange={(e) => setBatchPwd(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button variant="contained" color="warning" onClick={handleBatchCoinRecharge} disabled={batchLoading || batchItems.length === 0}>
          {batchLoading ? "处理中..." : `确认拨付（${batchItems.length}条）`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
